import { useMemo, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { normalizarChaveTela } from "../../layout/menuAcesso";
import { obterTodasChavesTelasDisponiveis } from "../../layout/telasDisponiveis";
import { useConfiguracoesSistemaPublicas } from "../../utils/configuracoesSistema";
import { usuarioTemAcessoTela } from "../../utils/permissoes";

/** Texto único do tooltip interno dos atalhos travados por falta de permissão. */
export const AVISO_SEM_PERMISSAO_TELA = "Você não tem permissão para acessar esta tela.";

/* Aparência de item desabilitado para atalhos que não têm uma classe própria de
   "bloqueado" no CSS da tela (cards de dashboard, links "Ver todas"). */
const ESTILO_DESABILITADO: CSSProperties = {
  opacity: 0.5,
  cursor: "not-allowed",
  textDecoration: "none",
};

/**
 * Checagem de acesso por rota, usada por qualquer atalho que leve a outra tela.
 * Segue a mesma regra da `RotaProtegida`: só telas controladas (as que aparecem
 * no cadastro de Perfil de Acesso) podem ser negadas — rotas auxiliares seguem
 * liberadas. Quem não tem permissão fica com o botão/link desabilitado, nunca
 * escondido, e nunca navegando para uma tela que ele não pode ver.
 */
export function useAcessoTela() {
  const { usuario } = useAuth();
  const { traduzir } = useIdioma();
  const configuracoes = useConfiguracoesSistemaPublicas();

  const telasControladas = useMemo(
    () => new Set(obterTodasChavesTelasDisponiveis(configuracoes)),
    [configuracoes]
  );

  const podeAbrirTela = useMemo(
    () => (rota: string) => {
      const chave = normalizarChaveTela(rota);
      if (!telasControladas.has(chave)) return true;
      return usuarioTemAcessoTela(usuario, chave);
    },
    [telasControladas, usuario]
  );

  /** A tela existe para esta organização (módulo ligado nas configurações). */
  const telaControlada = useMemo(
    () => (rota: string) => telasControladas.has(normalizarChaveTela(rota)),
    [telasControladas]
  );

  return { podeAbrirTela, telaControlada, avisoSemPermissao: traduzir(AVISO_SEM_PERMISSAO_TELA) };
}

interface LinkTelaProps {
  to: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
  children: ReactNode;
  onClick?: () => void;
  /** Repassado como está — algumas telas usam para o efeito de vidro. */
  "data-vidro"?: string;
}

/**
 * `<Link>` que vira um elemento desabilitado quando o perfil do login não tem a
 * tela de destino liberada. Mantém a mesma classe (e portanto o mesmo visual) e
 * explica no tooltip da plataforma por que não dá para clicar.
 */
export function LinkTela({ to, className, style, title, children, onClick, ...resto }: LinkTelaProps) {
  const { podeAbrirTela, avisoSemPermissao } = useAcessoTela();

  if (!podeAbrirTela(to)) {
    return (
      <span
        {...resto}
        className={[className, "tooltip"].filter(Boolean).join(" ")}
        style={{ ...style, ...ESTILO_DESABILITADO }}
        data-tooltip={avisoSemPermissao}
        aria-disabled="true"
        role="link"
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      {...resto}
      to={to}
      className={[className, title ? "tooltip" : ""].filter(Boolean).join(" ")}
      style={style}
      data-tooltip={title}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}
